import React, {useContext, useEffect} from 'react';
import {useDispatch} from 'react-redux';

import {RoleLink} from 'src/admin-rights-service';
import {UserLink} from 'src/components/links';
import {ProfileMain} from 'src/components/profile-tab-structure';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {RowNumbered} from 'src/components/row-numbered';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {ACCESS_RIGHTS_LABELS} from 'src/hooks/access-rights/constants';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {FormattedDate} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import {Pill} from '@pb/kit-component-strip-of-pills';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';
import {LocaleContext} from '@pb/kit-context-locale';


import {ADMIN_ROLE_SETTINGS_TABS} from '../constants';
import {UserRoleSettingsTabs} from '../user-role-settings-tabs';

import {PLACE} from './constants';
import {useGetUserRoleAuditLog} from './hooks/useGetUserRoleAuditLog';
import {UserRoleAuditLogPagination} from './pagination';
import {userRoleAuditLogStateActions} from './slices/user-role-audit-log-state.slice';


import type {AccessRight} from 'src/hooks/access-rights/constants';

type PillType = {name: AccessRight, variant: 'ADDITION' | 'EXCLUSION'};

type Props = {
    userId: number,
};

const ADDITION = 'ADDITION';
const EXCLUSION = 'EXCLUSION';

export const UserRoleAuditLog = ({userId}: Props) => {
    const dispatch = useDispatch();
    const {locale} = useContext(LocaleContext);
    const {
        loadingError,
        result,
        pagination: {currentPage, pageSize},
    } = useGetUserRoleAuditLog({userId});

    useEffect(() => {
        return () => {
            dispatch(userRoleAuditLogStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <UserRoleSettingsTabs userId={userId} activeSubTab={ADMIN_ROLE_SETTINGS_TABS.AUDIT_LOG.name}/>
            <ProfileMain>
                <ServerResponseHandler
                    serverError={loadingError}
                    accessRight={ACCESS_RIGHTS.MANAGE_PERMISSIONS_FOR_A_PROFILE as AccessRight}>
                    <BoxLoading loading={!result}>
                        <Group direction="v">
                            <Table borderedGrid={true}>
                                <Head>
                                    <Cell width={50}>
                                        <RowNumbered
                                            maxRowNumber={result?.totalCount || 0}
                                            placeholder={<strong>#</strong>}
                                        />
                                    </Cell>
                                    <Cell width={200}>Editor</Cell>
                                    <Cell width={200}>Change Date</Cell>
                                    <Cell width={200}>Admin Role</Cell>
                                    <Cell>Changed Permissions</Cell>
                                </Head>
                                <Body>
                                    {result?.data.map((res, index) => (
                                        <Row key={(currentPage - 1) * pageSize + index + 1}>
                                            <Cell width={50}>
                                                <RowNumbered
                                                    maxRowNumber={result?.totalCount || 0}
                                                    placeholder={(currentPage - 1) * pageSize + index + 1}
                                                />
                                            </Cell>
                                            <Cell>
                                                {res.editor ? (
                                                    <UserLink
                                                        userId={res.editor.userId}
                                                        caption={[
                                                            res.editor.firstName,
                                                            res.editor.lastName,
                                                        ].filter(Boolean).join(' ') || res.editor.email}
                                                        place={PLACE}
                                                    />
                                                ) : 'System'}
                                            </Cell>
                                            <Cell>
                                                {FormattedDate.format({
                                                    value: res.eventTime,
                                                    format: mapDateFormat(locale.dateFormat, 'WORD', 'DATE_TIME'),
                                                }) || ''}
                                            </Cell>
                                            <Cell>
                                                {res.currentProfile ? (
                                                    <RoleLink
                                                        profileId={res.currentProfile?.id}
                                                        caption={(!res.currentProfile.id && res.currentProfile.name)
                                                            ? undefined
                                                            : res.currentProfile.name}
                                                    />
                                                ) : 'None'}
                                            </Cell>
                                            <Cell>
                                                <ResponsiveElementGroup padding="0 8px 4px 0">
                                                    {[
                                                        ...res.expandedRights?.map(permission => ({
                                                            name: permission as AccessRight,
                                                            variant: ADDITION as 'ADDITION',
                                                        })) || [],
                                                        ...res.restrictedRights?.map(permission => ({
                                                            name: permission as AccessRight,
                                                            variant: EXCLUSION as 'EXCLUSION',
                                                        })) || [],
                                                    ].map(({name, variant}: PillType) => (
                                                        <Pill
                                                            key={name}
                                                            name={ACCESS_RIGHTS_LABELS[
                                                                name as keyof typeof ACCESS_RIGHTS_LABELS
                                                            ]}
                                                            variant={variant}
                                                        />
                                                    ))}
                                                </ResponsiveElementGroup>
                                            </Cell>
                                        </Row>
                                    ))}
                                    {!!result && !result.data.length && (
                                        <Row>
                                            <Cell colSpan={5} className="text-align_center">
                                                The user has no role changes yet
                                            </Cell>
                                        </Row>
                                    )}
                                </Body>
                            </Table>
                            <UserRoleAuditLogPagination totalCount={result?.totalCount || 0}/>
                        </Group>
                    </BoxLoading>
                </ServerResponseHandler>
            </ProfileMain>
        </>
    );
};
